/**
 * MonEspaceConfigPage — configuration de la brique « Mon dossier personnel » (253).
 *
 * ─── CE QUE LA MAIRIE RÈGLE ICI ─────────────────────────────────────────────
 *
 *   · les CATÉGORIES : les intitulés sous lesquels les liens sont rangés
 *     (item 6.4, « libellés paramétrables ») ;
 *   · les LIENS : outils, notices, sites que les agents utilisent au quotidien ;
 *   · les NUMÉROS D'URGENCE : l'annuaire du plan communal de sauvegarde
 *     (item 8.11, art. L731-3 CSI).
 *
 * ─── DÉSACTIVER PLUTÔT QUE SUPPRIMER ────────────────────────────────────────
 *
 * Les liens et les contacts portent une bascule `actif`. Un numéro d'astreinte
 * hors saison se désactive : il disparaît de l'espace agent, il reste ici. La
 * suppression existe, mais elle est un geste distinct et confirmé.
 *
 * ⚠️ Supprimer une CATÉGORIE ne supprime pas ses liens : ils basculent dans
 * « Divers ». Perdre des liens en renommant un onglet serait une très mauvaise
 * surprise pour une secrétaire de mairie.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Card, Alert, Button, Input, Field, Badge, ModuleHeader,
} from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { AppHeader } from '@/components/AppHeader';
import { api, ApiException } from '@/lib/api';
import type {
  AgentsLienCategorie, AgentsLienUtile, AgentsContactUrgence,
} from '@roronoa-collectivites/shared-types';
import {
  Settings2, Plus, Trash2, Eye, EyeOff, Siren, LinkIcon, Layers, Loader2,
} from 'lucide-react';

type Onglet = 'liens' | 'urgences' | 'categories';

export function MonEspaceConfigPage() {
  const { confirm, dialogElement } = useConfirmDialog();

  const [onglet, setOnglet] = useState<Onglet>('liens');
  const [categories, setCategories] = useState<AgentsLienCategorie[]>([]);
  const [liens, setLiens] = useState<AgentsLienUtile[]>([]);
  const [urgences, setUrgences] = useState<AgentsContactUrgence[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Saisie — un jeu de champs par onglet, remis à zéro après création.
  const [catLibelle, setCatLibelle] = useState('');
  const [lienLibelle, setLienLibelle] = useState('');
  const [lienUrl, setLienUrl] = useState('');
  const [lienDescription, setLienDescription] = useState('');
  const [lienCategorie, setLienCategorie] = useState('');
  const [urgLibelle, setUrgLibelle] = useState('');
  const [urgOrganisme, setUrgOrganisme] = useState('');
  const [urgTelephone, setUrgTelephone] = useState('');
  const [urgRolePcs, setUrgRolePcs] = useState('');

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // `tous: true` — l'écran de configuration montre aussi ce qui est
      // désactivé, sinon on ne pourrait jamais le réactiver.
      const [c, l, u] = await Promise.all([
        api.monEspace.listCategories(),
        api.monEspace.listLiens(true),
        api.monEspace.listUrgences(true),
      ]);
      setCategories(c); setLiens(l); setUrgences(u);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function agir(action: () => Promise<unknown>, succes: string) {
    setSaving(true); setError(null); setMsg(null);
    try {
      await action();
      setMsg(succes);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Opération impossible');
    } finally { setSaving(false); }
  }

  const ongletsMeta: { key: Onglet; label: string; icon: typeof LinkIcon }[] = [
    { key: 'liens', label: 'Liens utiles', icon: LinkIcon },
    { key: 'urgences', label: 'Numéros d\'urgence', icon: Siren },
    { key: 'categories', label: 'Catégories', icon: Layers },
  ];

  return (
    <div className="min-h-screen text-ink">
      {/* Le bandeau mairie manquait : sans lui, aucun retour au tableau de bord
          depuis ces écrans. Remonté par la mairie le 28/07. */}
      <AppHeader module="Mon dossier personnel" accent="rh" />
      <ModuleHeader
        accent="rh"
        icon={Settings2}
        eyebrow="Paramétrage"
        title="Mon dossier personnel"
      />

      <main id="main-content" className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        {error && <Alert variant="error" className="mb-4">{error}</Alert>}
        {msg && <Alert variant="success" className="mb-4">{msg}</Alert>}

        <nav className="flex flex-wrap gap-2" aria-label="Sections de configuration">
          {ongletsMeta.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setOnglet(key)}
              aria-current={onglet === key ? 'page' : undefined}
              className={`inline-flex h-9 items-center gap-2 rounded-r-lg border px-3 text-sm transition-colors ${
                onglet === key
                  ? 'border-rh/50 bg-rh/10 font-medium text-ink'
                  : 'border-border bg-surface/50 text-ink-muted hover:border-border-strong'
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>

        {loading ? (
          <div className="flex items-center gap-2 py-12 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Chargement…
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            {/* ── Liens utiles ──────────────────────────────────────────── */}
            {onglet === 'liens' && (
              <>
                <Card variant="glass" padded>
                  <h2 className="font-medium text-ink">Ajouter un lien</h2>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Field label="Libellé" htmlFor="lien-libelle" required>
                      <Input
                        id="lien-libelle" value={lienLibelle}
                        onChange={(e) => setLienLibelle(e.target.value)}
                        placeholder="Portail Légifrance"
                      />
                    </Field>
                    <Field
                      label="Adresse" htmlFor="lien-url" required
                      hint="Doit commencer par http:// ou https://"
                    >
                      <Input
                        id="lien-url" value={lienUrl}
                        onChange={(e) => setLienUrl(e.target.value)}
                        placeholder="https://…"
                      />
                    </Field>
                    <Field label="Description" htmlFor="lien-desc">
                      <Input
                        id="lien-desc" value={lienDescription}
                        onChange={(e) => setLienDescription(e.target.value)}
                        placeholder="À quoi ça sert, en une ligne"
                      />
                    </Field>
                    <Field label="Catégorie" htmlFor="lien-cat">
                      <select
                        id="lien-cat" value={lienCategorie}
                        onChange={(e) => setLienCategorie(e.target.value)}
                        className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg"
                      >
                        <option value="">— Aucune (rangé dans « Divers ») —</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>{c.libelle}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="mt-3">
                    <Button
                      variant="rh" size="sm" loading={saving}
                      disabled={!lienLibelle.trim() || !lienUrl.trim()}
                      onClick={() => void agir(async () => {
                        await api.monEspace.creerLien({
                          libelle: lienLibelle.trim(),
                          url: lienUrl.trim(),
                          description: lienDescription.trim() || null,
                          categorieId: lienCategorie || null,
                        });
                        setLienLibelle(''); setLienUrl('');
                        setLienDescription(''); setLienCategorie('');
                      }, 'Lien ajouté.')}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter
                    </Button>
                  </div>
                </Card>

                {liens.length === 0 ? (
                  <Card variant="glass" padded>
                    <p className="text-sm text-ink-muted">Aucun lien pour le moment.</p>
                  </Card>
                ) : (
                  <ul className="space-y-2">
                    {liens.map((l) => (
                      <li key={l.id}>
                        <Card variant="glass" padded>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-ink">{l.libelle}</span>
                                {l.categorieLibelle && (
                                  <Badge variant="neutral" size="sm">{l.categorieLibelle}</Badge>
                                )}
                                {!l.actif && <Badge variant="urgent" size="sm">Masqué</Badge>}
                              </div>
                              <div className="mt-0.5 truncate text-xs text-ink-subtle">{l.url}</div>
                              {l.description && (
                                <p className="mt-1 text-xs text-ink-muted">{l.description}</p>
                              )}
                            </div>
                            <div className="flex flex-shrink-0 gap-1.5">
                              <Button
                                variant="glass" size="sm" disabled={saving}
                                aria-label={l.actif ? `Masquer ${l.libelle}` : `Afficher ${l.libelle}`}
                                onClick={() => void agir(
                                  () => api.monEspace.modifierLien(l.id, {
                                    libelle: l.libelle, url: l.url,
                                    description: l.description,
                                    categorieId: l.categorieId,
                                    actif: !l.actif,
                                  }),
                                  l.actif ? 'Lien masqué.' : 'Lien affiché.'
                                )}
                              >
                                {l.actif
                                  ? <EyeOff className="h-4 w-4" aria-hidden="true" />
                                  : <Eye className="h-4 w-4" aria-hidden="true" />}
                              </Button>
                              <Button
                                variant="glass" size="sm" disabled={saving}
                                aria-label={`Supprimer ${l.libelle}`}
                                onClick={async () => {
                                  const ok = await confirm({
                                    title: 'Supprimer ce lien ?',
                                    description: `« ${l.libelle} » disparaîtra de l'espace agent. Cette action est définitive.`,
                                    confirmLabel: 'Supprimer', danger: true,
                                  });
                                  if (ok) void agir(() => api.monEspace.supprimerLien(l.id), 'Lien supprimé.');
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-urgent" aria-hidden="true" />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {/* ── Numéros d'urgence ─────────────────────────────────────── */}
            {onglet === 'urgences' && (
              <>
                <Alert variant="info">
                  Ces numéros s'affichent en tête de l'espace agent, avant les liens :
                  en situation de crise, personne ne fait défiler l'écran.
                </Alert>

                <Card variant="glass" padded>
                  <h2 className="font-medium text-ink">Ajouter un contact</h2>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Field label="Libellé" htmlFor="urg-libelle" required>
                      <Input
                        id="urg-libelle" value={urgLibelle}
                        onChange={(e) => setUrgLibelle(e.target.value)}
                        placeholder="Astreinte élus"
                      />
                    </Field>
                    <Field label="Téléphone" htmlFor="urg-tel" required>
                      <Input
                        id="urg-tel" value={urgTelephone} inputMode="tel"
                        onChange={(e) => setUrgTelephone(e.target.value)}
                        placeholder="06 00 00 00 00"
                      />
                    </Field>
                    <Field label="Organisme" htmlFor="urg-orga">
                      <Input
                        id="urg-orga" value={urgOrganisme}
                        onChange={(e) => setUrgOrganisme(e.target.value)}
                        placeholder="Mairie · SDIS · Préfecture…"
                      />
                    </Field>
                    <Field
                      label="Rôle dans le PCS" htmlFor="urg-role"
                      hint="Texte libre : chaque plan a sa propre nomenclature"
                    >
                      <Input
                        id="urg-role" value={urgRolePcs}
                        onChange={(e) => setUrgRolePcs(e.target.value)}
                        placeholder="Référent inondation"
                      />
                    </Field>
                  </div>
                  <div className="mt-3">
                    <Button
                      variant="rh" size="sm" loading={saving}
                      disabled={!urgLibelle.trim() || !urgTelephone.trim()}
                      onClick={() => void agir(async () => {
                        await api.monEspace.creerUrgence({
                          libelle: urgLibelle.trim(),
                          telephone: urgTelephone.trim(),
                          organisme: urgOrganisme.trim() || null,
                          rolePcs: urgRolePcs.trim() || null,
                        });
                        setUrgLibelle(''); setUrgTelephone('');
                        setUrgOrganisme(''); setUrgRolePcs('');
                      }, 'Contact ajouté.')}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter
                    </Button>
                  </div>
                </Card>

                {urgences.length === 0 ? (
                  <Card variant="glass" padded>
                    <p className="text-sm text-ink-muted">
                      Aucun numéro enregistré. La liste du plan communal de sauvegarde
                      se saisit ici, ligne par ligne.
                    </p>
                  </Card>
                ) : (
                  <ul className="space-y-2">
                    {urgences.map((c) => (
                      <li key={c.id}>
                        <Card variant="glass" padded>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-ink">{c.libelle}</span>
                                {c.rolePcs && <Badge variant="urgent" size="sm">{c.rolePcs}</Badge>}
                                {!c.actif && <Badge variant="neutral" size="sm">Masqué</Badge>}
                              </div>
                              <div className="mt-0.5 text-xs text-ink-subtle">
                                {c.telephone}
                                {c.organisme && ` · ${c.organisme}`}
                              </div>
                            </div>
                            <div className="flex flex-shrink-0 gap-1.5">
                              <Button
                                variant="glass" size="sm" disabled={saving}
                                aria-label={c.actif ? `Masquer ${c.libelle}` : `Afficher ${c.libelle}`}
                                onClick={() => void agir(
                                  () => api.monEspace.modifierUrgence(c.id, {
                                    libelle: c.libelle, telephone: c.telephone,
                                    organisme: c.organisme, rolePcs: c.rolePcs,
                                    telephoneSecondaire: c.telephoneSecondaire,
                                    note: c.note, actif: !c.actif,
                                  }),
                                  c.actif ? 'Contact masqué.' : 'Contact affiché.'
                                )}
                              >
                                {c.actif
                                  ? <EyeOff className="h-4 w-4" aria-hidden="true" />
                                  : <Eye className="h-4 w-4" aria-hidden="true" />}
                              </Button>
                              <Button
                                variant="glass" size="sm" disabled={saving}
                                aria-label={`Supprimer ${c.libelle}`}
                                onClick={async () => {
                                  const ok = await confirm({
                                    title: 'Supprimer ce contact ?',
                                    description: `« ${c.libelle} » disparaîtra de l'annuaire d'urgence. Cette action est définitive.`,
                                    confirmLabel: 'Supprimer', danger: true,
                                  });
                                  if (ok) void agir(() => api.monEspace.supprimerUrgence(c.id), 'Contact supprimé.');
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-urgent" aria-hidden="true" />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {/* ── Catégories ────────────────────────────────────────────── */}
            {onglet === 'categories' && (
              <>
                <Alert variant="info">
                  Supprimer une catégorie ne supprime pas ses liens : ils basculent
                  dans « Divers ».
                </Alert>

                <Card variant="glass" padded>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[240px] flex-1">
                      <Field label="Nouvelle catégorie" htmlFor="cat-libelle" required>
                        <Input
                          id="cat-libelle" value={catLibelle}
                          onChange={(e) => setCatLibelle(e.target.value)}
                          placeholder="Sécurité · Ressources · Outils métier…"
                        />
                      </Field>
                    </div>
                    <Button
                      variant="rh" size="sm" loading={saving}
                      disabled={!catLibelle.trim()}
                      onClick={() => void agir(async () => {
                        await api.monEspace.creerCategorie({ libelle: catLibelle.trim() });
                        setCatLibelle('');
                      }, 'Catégorie créée.')}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" /> Créer
                    </Button>
                  </div>
                </Card>

                {categories.length === 0 ? (
                  <Card variant="glass" padded>
                    <p className="text-sm text-ink-muted">
                      Aucune catégorie. Les liens seront tous rangés sous « Divers ».
                    </p>
                  </Card>
                ) : (
                  <ul className="space-y-2">
                    {categories.map((c) => {
                      const nb = liens.filter((l) => l.categorieId === c.id).length;
                      return (
                        <li key={c.id}>
                          <Card variant="glass" padded>
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <span className="font-medium text-ink">{c.libelle}</span>
                                <span className="ml-2 text-xs text-ink-subtle">
                                  {nb} lien{nb !== 1 ? 's' : ''}
                                </span>
                              </div>
                              <Button
                                variant="glass" size="sm" disabled={saving}
                                aria-label={`Supprimer la catégorie ${c.libelle}`}
                                onClick={async () => {
                                  const ok = await confirm({
                                    title: 'Supprimer cette catégorie ?',
                                    description: nb > 0
                                      ? `${nb} lien${nb !== 1 ? 's' : ''} basculeront dans « Divers ». Aucun lien ne sera perdu.`
                                      : 'Cette catégorie ne contient aucun lien.',
                                    confirmLabel: 'Supprimer', danger: true,
                                  });
                                  if (ok) void agir(() => api.monEspace.supprimerCategorie(c.id), 'Catégorie supprimée.');
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-urgent" aria-hidden="true" />
                              </Button>
                            </div>
                          </Card>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
      </main>
      {dialogElement}
    </div>
  );
}
